import { GalleryService } from "features/gallery/service/gallery.service"
import { GalleryMedium } from "features/gallery/gallery.types"
import { GalleryThumbnailAction } from "features/gallery/thumbnail/GalleryThumbnail.vue"
import { ref, Ref, watch } from "vue"
import { Highlight } from "suus-api"

/**
 * This service is responsible for managing the state of the thumbnails.
 * Pass a "BuildThumbnailRepresentationFunction" which is responsible for building the thumbnail representation.
 * A thumbnail representation determines which actions are displayed on the thumbnail. see: [ThumbnailRepresentation]
 * The build function is called in the following cases:
 * - the thumbnail is registered
 * - the thumbnail is added to highlights
 * - the thumbnail is removed from highlights
 * - the user's edit rights change
 */
export class ThumbnailService {
  protected _thumbnails: ThumbnailRepresentationMap = new Map<
    number,
    ThumbnailRepresentation
  >()

  constructor(
    protected galleryService: GalleryService,
    public buildThumbnailRepresentation: BuildThumbnailRepresentationFunction
  ) {
    galleryService.registerAddListener(this._onAdd.bind(this))
    galleryService.registerRemoveListener(this._onRemove.bind(this))
    watch(
      () => galleryService.canEdit.value,
      this._onCanEditChange.bind(this),
      { immediate: true }
    )
  }

  /**
   * Register a thumbnail for a gallery medium. This function should be called, when a GalleryMedium is reloaded from the server.
   * This way the state stays consistent.
   * @param galleryMedium
   */
  registerThumbnail(galleryMedium: GalleryMedium): ThumbnailRepresentation {
    if (this._thumbnails.has(galleryMedium.id)) {
      const existingThumbnail = this._thumbnails.get(galleryMedium.id)
      existingThumbnail.isHighlight.value = !!galleryMedium.links?.highlight_url
      return this._buildThumbnailRepresentation(existingThumbnail, null)
    }
    const thumbnailRepresentation = this._buildThumbnailRepresentation(
      new ThumbnailRepresentation(galleryMedium),
      null
    )
    this._thumbnails.set(galleryMedium.id, thumbnailRepresentation)
    return thumbnailRepresentation
  }

  private _buildThumbnailRepresentation(
    thumbnailRepresentation: ThumbnailRepresentation,
    canEdit: boolean | null
  ): ThumbnailRepresentation {
    const updatedThumbnailRepresentation = this.buildThumbnailRepresentation(
      thumbnailRepresentation,
      canEdit ?? this.galleryService.canEdit.value
    )
    thumbnailRepresentation.update(updatedThumbnailRepresentation)
    return thumbnailRepresentation
  }

  private _onAdd(highlight: Highlight) {
    const addedGalleryMedium = highlight.event_medium
    const thumbnailRepresentation = this._thumbnails.get(addedGalleryMedium.id)
    if (thumbnailRepresentation) {
      thumbnailRepresentation.isHighlight.value = true
      this._buildThumbnailRepresentation(thumbnailRepresentation, null)
    }
  }

  private _onRemove(galleryId: number) {
    const thumbnailRepresentation = this._thumbnails.get(galleryId)
    if (thumbnailRepresentation) {
      thumbnailRepresentation.isHighlight.value = false
      this._buildThumbnailRepresentation(thumbnailRepresentation, null)
    }
  }

  private _onCanEditChange(canEdit: boolean) {
    this._thumbnails.forEach((thumbnailRepresentation) => {
      this._buildThumbnailRepresentation(thumbnailRepresentation, canEdit)
    })
  }
}

export class ThumbnailRepresentation {
  galleryMedium: GalleryMedium
  topLeftAction: Ref<GalleryThumbnailAction | null>
  topRightAction: Ref<GalleryThumbnailAction | null>
  isHighlight: Ref<boolean> = ref(false)
  constructor(galleryMedium: GalleryMedium) {
    this.galleryMedium = galleryMedium
    this.topLeftAction = ref(null)
    this.topRightAction = ref(null)
    this.isHighlight.value = !!galleryMedium.links?.highlight_url
  }

  update(other: ThumbnailRepresentation) {
    this.topLeftAction.value = other.topLeftAction.value
    this.topRightAction.value = other.topRightAction.value
    this.isHighlight.value = other.isHighlight.value
  }
}

type ThumbnailRepresentationMap = Map<number, ThumbnailRepresentation>

export type BuildThumbnailRepresentationFunction = (
  thumbnailRepresentation: ThumbnailRepresentation,
  canEdit: boolean
) => ThumbnailRepresentation
